import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './ImagePreview.less';

const placeholderMarks = ['default-store.png', 'default-line-item.png'];

export const isDefaultPlaceholderImage = (url?: string) => {
  if (!url) {
    return true;
  }
  return placeholderMarks.some(mark => url.indexOf(mark) !== -1);
};

interface IImagePreviewProps {
  src: string;
  onClose: () => void;
}

const ImagePreview: React.FC<IImagePreviewProps> = ({ src, onClose }) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [side, setSide] = useState<number | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);

  const fitImage = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) {
      return;
    }

    setSide(Math.max(1, Math.floor(Math.min(frame.clientWidth, frame.clientHeight))));
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', fitImage);
    fitImage();
    const image = imageRef.current;
    if (image && image.complete && image.naturalWidth) {
      setLoaded(true);
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', fitImage);
    };
  }, [onClose, fitImage]);

  return ReactDOM.createPortal(
    <div className="bm-image-preview" onClick={onClose}>
      <button className="bm-image-preview-close" type="button" onClick={onClose}>×</button>
      <div className="bm-image-preview-frame" ref={frameRef}>
        <img
          ref={imageRef}
          className={`bm-image-preview-img ${side && loaded ? 'is-fitted' : ''}`}
          src={src}
          alt=""
          style={side ? { width: side, height: side } : undefined}
          onLoad={() => setLoaded(true)}
        />
      </div>
    </div>,
    document.body,
  );
};

export default ImagePreview;
